import { useEffect, useState, type FormEvent } from 'react';
import { validateWorkspaceName } from '@floorpoll/shared';
import { Field, FormError, FormNotice } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useWorkspace } from '../../lib/workspace.js';

/** Story #12 — rename the workspace. Admin only. */
export function Settings() {
  const { active, can } = useWorkspace();
  const { refreshMe } = useAuth();
  const [name, setName] = useState(active?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(active?.name ?? '');
  }, [active?.name]);

  if (!active) return <p className="muted">No workspace selected.</p>;

  if (!can('workspace:manage')) {
    return (
      <section>
        <h1>Workspace settings</h1>
        <p className="muted">Only an admin can change workspace settings.</p>
      </section>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    const issues = validateWorkspaceName(name);
    if (issues.length > 0) {
      setError(issues[0]?.message ?? 'That name is not valid');
      return;
    }

    setBusy(true);
    try {
      await apiRequest(`/workspaces/${active!.workspaceId}`, {
        method: 'PATCH',
        body: { name: name.trim() },
      });
      // The switcher reads names from /me, so refresh it too.
      await refreshMe();
      setNotice('Workspace renamed.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not rename the workspace.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h1>Workspace settings</h1>

      <form className="panel" onSubmit={onSubmit} noValidate>
        <Field label="Workspace name" htmlFor="workspace-name">
          <input
            id="workspace-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Field>

        <FormError message={error} />
        <FormNotice message={notice} />

        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}
